import { useEffect, useMemo, useRef, useState } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { InboxRow } from '../lib/types';
import { Badge, Button, EmptyState, Input, Select, Spinner } from '../components/ui';
import { Icon, type IconName } from '../components/icons';
import { ResponseView } from '../components/ResponseView';
import { useActiveCompany } from '../components/CompanySwitcher';
import { relative } from './FormList';

/**
 * Every response, newest first, without choosing a form first.
 *
 * The per-form Responses tab answers "how is this form doing". It cannot answer the
 * question people actually arrive with — "what came in?" — because that question spans
 * forms, and answering it meant opening each form in turn. This is the other half: one
 * feed over the whole workspace, filtered and searched in one place, with the response
 * itself alongside rather than behind another click.
 *
 * The selected response lives in the URL rather than in state, so a row can be sent to a
 * colleague, kept in a tab, or reopened tomorrow. The filters do too — a saved search is
 * just a bookmark.
 *
 * The date range defaults to today. Arriving at a workspace with a year of responses and
 * being shown all of them answers "what exists", not "what came in", and the feed exists
 * to answer the second. `ANY` is what widening back to everything looks like in the URL:
 * an absent parameter means "not chosen yet, so today", and only an explicit value can
 * say "no bound", or a bookmarked all-time search would silently narrow to today on
 * being reopened.
 */

/** A date input wants `yyyy-mm-dd` in the viewer's own day, not UTC's. */
function isoDay(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
}

/** The value a date parameter carries when somebody has cleared the bound on purpose. */
const ANY = 'any';
export function Inbox() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { active } = useActiveCompany();
  const listRef = useRef<HTMLDivElement>(null);

  // Recomputed per render rather than per day: a tab left open overnight should not have
  // its "Today" quietly become yesterday, and the cost is one Date.
  const today = isoDay(new Date());

  const filters = useMemo(() => {
    const bound = (key: 'from' | 'to') => {
      const raw = params.get(key);
      if (raw === null) return today;    // untouched — today
      if (raw === ANY) return '';        // widened on purpose — no bound
      return raw;
    };

    return {
      q: params.get('q') ?? '',
      formId: params.get('formId') ?? '',
    questionRef: params.get('questionRef') ?? '',
      status: params.get('status') ?? '',
      channel: params.get('channel') ?? '',
      from: bound('from'),
      to: bound('to'),
    };
  }, [params, today]);

  // The box is typed into far faster than the server should be asked. Local state keeps
  // it responsive; the URL — and so the query — only catches up when typing pauses.
  const [term, setTerm] = useState(filters.q);
  useEffect(() => { setTerm(filters.q); }, [filters.q]);

  useEffect(() => {
    if (term === filters.q) return;
    const timer = setTimeout(() => setFilter('q', term), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  }

  // Both bounds move together so a preset is one history entry rather than two, and an
  // emptied field becomes ANY rather than an absent parameter — see the note above.
  function setRange(from: string, to: string) {
    const next = new URLSearchParams(params);
    next.set('from', from || ANY);
    next.set('to', to || ANY);
    setParams(next, { replace: true });
  }

  const ranges = useMemo(() => {
    const back = (days: number) => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return isoDay(date);
    };
    return [
      { key: 'today', label: 'Today', from: today, to: today },
      { key: '7', label: '7 days', from: back(6), to: today },
      { key: '30', label: '30 days', from: back(29), to: today },
      { key: 'all', label: 'All time', from: '', to: '' },
    ];
  }, [today]);

  const range = ranges.find((r) => r.from === filters.from && r.to === filters.to);

  const forms = useQuery({
    queryKey: ['forms', 'inbox-filter', active?.id ?? 'all'],
    queryFn: () => api.forms.list(active ? { companyId: active.id } : {}),
    staleTime: 60_000,
  });

  const feed = useInfiniteQuery({
    queryKey: ['inbox', filters, active?.id ?? 'all'],
    queryFn: ({ pageParam }) => api.inbox.list({
      ...filters,
      companyId: active?.id,
      cursor: pageParam,
      limit: 50,
    }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,

    // Responses arrive while somebody is looking at this. A feed that needs a manual
    // refresh to show what came in two minutes ago is not a feed.
    //
    // Only while a single page is loaded, though: react-query refetches every loaded page
    // together, so somebody who had paged back through a month of responses would fire a
    // request per page every twenty seconds to learn about rows that only ever appear at
    // the top.
    refetchInterval: (query) =>
      (query.state.data?.pages.length ?? 1) === 1 ? 20_000 : false,
    refetchIntervalInBackground: false,
  });

  const rows = useMemo(
    () => feed.data?.pages.flatMap((page) => page.data) ?? [],
    [feed.data],
  );

  const selected = responseId ?? null;

  function open(id: string | null) {
    const query = params.toString();
    navigate(id ? `/responses/${id}${query ? `?${query}` : ''}`
                : `/responses${query ? `?${query}` : ''}`);
  }

  // Arrow keys and j/k move down the feed the way a mail client does. Guarded on the
  // event target so they stay typeable inside the search box and the date fields.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'Escape' && selected) { open(null); return; }

      const step = event.key === 'ArrowDown' || event.key === 'j' ? 1
        : event.key === 'ArrowUp' || event.key === 'k' ? -1
          : 0;

      if (step === 0 || rows.length === 0) return;
      event.preventDefault();

      const current = rows.findIndex((row) => row.id === selected);
      const next = current < 0
        ? (step > 0 ? 0 : rows.length - 1)
        : Math.min(Math.max(current + step, 0), rows.length - 1);

      const nextRow = rows[next];
      if (!nextRow) return;

      open(nextRow.id);
      listRef.current
        ?.querySelector(`[data-response="${nextRow.id}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selected, params]);

  // What has been narrowed by hand. Today's date range is where everybody starts, so it
  // is not something to offer to clear — but it is still a reason the feed can look empty,
  // which is why the empty state asks about it separately.
  const narrowed = [filters.q, filters.formId, filters.status, filters.channel]
    .filter(Boolean).length + (range?.key === 'today' ? 0 : 1);

  const filtered = narrowed > 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ---- header and filters --------------------------------------------------- */}
      <div className="shrink-0 border-b border-[var(--ff-border)] bg-[var(--ff-surface)]
                      px-5 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-base font-semibold">
              Responses
              {feed.isFetching && !feed.isFetchingNextPage && (
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--ff-primary)]"
                      title="Checking for new responses" />
              )}
            </h1>
            <p className="text-xs text-[var(--ff-text-muted)]">
              {feed.isLoading
                ? 'Loading…'
                : `${rows.length}${feed.hasNextPage ? '+' : ''} `
                  + `${rows.length === 1 ? 'response' : 'responses'}`
                  + `${active ? ` in ${active.name}` : ' across every form'}`
                  + ' · refreshes automatically'}
            </p>
          </div>

          <div className="ml-auto flex w-full items-center gap-2 sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Icon
                name="search"
                size={15}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2
                           text-[var(--ff-text-muted)]"
              />
              <Input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search answers, response id or form"
                aria-label="Search responses"
                className="pl-8"
              />
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-lg border
                        border-[var(--ff-border)] bg-[var(--ff-surface-raised)] p-1.5">
          <FilterSelect
            icon="page"
            label="Filter by form"
            value={filters.formId}
            onChange={(e) => setFilter('formId', e.target.value)}
            className="w-full sm:w-56"
          >
            <option value="">Every form</option>
            {forms.data?.data.map((form) => (
              <option key={form.id} value={form.id}>{form.name}</option>
            ))}
          </FilterSelect>

          <FilterSelect
            icon="checkbox"
            label="Filter by status"
            value={filters.status}
            onChange={(e) => setFilter('status', e.target.value)}
            className="w-[calc(50%-0.1875rem)] sm:w-36"
          >
            <option value="">Any status</option>
            <option value="Complete">Complete</option>
            <option value="Partial">Partial</option>
            <option value="Flagged">Flagged</option>
            <option value="Test">Test</option>
          </FilterSelect>

          <FilterSelect
            icon="switch"
            label="Filter by channel"
            value={filters.channel}
            onChange={(e) => setFilter('channel', e.target.value)}
            className="w-[calc(50%-0.1875rem)] sm:w-36"
          >
            <option value="">Any channel</option>
            <option value="Link">Link</option>
            <option value="Embed">Embed</option>
            <option value="Api">API</option>
            <option value="Email">Email</option>
            <option value="Import">Import</option>
            <option value="Qr">QR</option>
          </FilterSelect>

          <span aria-hidden="true"
                className="mx-0.5 hidden h-6 w-px bg-[var(--ff-border)] lg:block" />

          {/*
            The four ranges people actually ask for, one click each. The date fields stay
            for everything else — presets that hide the exact dates leave somebody who
            needs one Tuesday in March with nowhere to type it.
          */}
          <div role="group" aria-label="Date range" className="flex h-9 items-center gap-0.5
                          rounded-md border border-[var(--ff-border-strong)]
                          bg-[var(--ff-surface)] p-0.5">
            {ranges.map((preset) => {
              const on = range?.key === preset.key;
              return (
                <button
                  key={preset.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setRange(preset.from, preset.to)}
                  className={clsx(
                    'rounded px-2 py-1 text-xs font-medium transition-colors duration-150',
                    on
                      ? 'bg-[var(--ff-primary)] text-[var(--ff-on-primary)]'
                      : 'text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)]'
                        + ' hover:text-[var(--ff-text)]',
                  )}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          <div className={clsx(
            'flex h-9 items-center gap-2 rounded-md border bg-[var(--ff-surface)] px-2.5 text-xs',
            range?.key ? 'border-[var(--ff-border-strong)]' : 'border-[var(--ff-primary)]',
          )}>
            <Icon name="calendar" size={14} className="shrink-0 text-[var(--ff-text-faint)]" />
            <input
              type="date"
              className="ff-date w-[7.5rem] text-xs"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => setRange(e.target.value, filters.to)}
              aria-label="Submitted on or after"
            />
            <span aria-hidden="true" className="text-[var(--ff-text-faint)]">–</span>
            <input
              type="date"
              className="ff-date w-[7.5rem] text-xs"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => setRange(filters.from, e.target.value)}
              aria-label="Submitted on or before"
            />
          </div>

          {/* Arrives from a dashboard link rather than from a control here, so it has to
              announce itself — an invisible filter reads as missing data. */}
          {filters.questionRef && (
            <span className="flex items-center gap-1 rounded-full bg-[var(--ff-surface-sunken)]
                             px-2 py-1 text-xs">
              answered <code className="font-mono">{filters.questionRef}</code>
              <button
                type="button"
                aria-label="Remove the question filter"
                onClick={() => setFilter('questionRef', '')}
                className="text-[var(--ff-text-muted)] hover:text-[var(--ff-text)]"
              >
                <Icon name="close" size={12} />
              </button>
            </span>
          )}

          {filtered && (
            <Button
              variant="ghost"
              className="ml-auto min-h-8 gap-1 px-2 py-1 text-xs"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
            >
              <Icon name="close" size={13} />
              Clear {narrowed} {narrowed === 1 ? 'filter' : 'filters'}
            </Button>
          )}
        </div>
      </div>

      {/* ---- feed and reading pane ------------------------------------------------- */}
      <div className="flex min-h-0 flex-1">
        <div
          ref={listRef}
          className={clsx(
            'min-h-0 overflow-auto',
            selected ? 'hidden lg:block lg:w-[42%] lg:border-r' : 'flex-1',
            'border-[var(--ff-border)]',
          )}
        >
          {feed.isLoading ? (
            <div className="p-8"><Spinner label="Loading responses" /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              title={filtered
                ? 'Nothing matches those filters'
                : 'Nothing has arrived today'}
              description={filtered
                ? 'Try a wider date range, or clear the filters to see everything that has arrived.'
                : 'The feed opens on today. Widen the date range to see what came in earlier — '
                  + 'or publish a form and share its link, and everything that arrives lands '
                  + 'here, whichever form it came from.'}
            />
          ) : (
            <>
              <ul>
                {rows.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    selected={row.id === selected}
                    compact={Boolean(selected)}
                    onOpen={() => open(row.id)}
                  />
                ))}
              </ul>

              {feed.hasNextPage && (
                <div className="p-3">
                  <Button
                    className="w-full"
                    onClick={() => feed.fetchNextPage()}
                    disabled={feed.isFetchingNextPage}
                  >
                    {feed.isFetchingNextPage ? 'Loading…' : 'Load older responses'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {selected && (
          <div className="min-h-0 flex-1 bg-[var(--ff-surface)]">
            <ResponseView
              responseId={selected}
              onClose={() => open(null)}
              onNavigate={(id) => open(id)}
            />
          </div>
        )}

        {!selected && rows.length > 0 && (
          <div className="hidden min-h-0 flex-1 items-center justify-center border-l
                          border-[var(--ff-border)] lg:flex">
            <p className="max-w-xs px-6 text-center text-sm text-[var(--ff-text-muted)]">
              Select a response to read it. Use <kbd className="font-mono">↑</kbd> and{' '}
              <kbd className="font-mono">↓</kbd> to move through the feed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One filter in the bar: an icon that says what is being narrowed, and the select itself.
 *
 * The icon sits inside the control rather than beside it as a text label. Three labelled
 * pairs took the whole width of the bar and pushed the dates onto their own line; the
 * placeholder option ("Every form") already reads as the label while nothing is chosen,
 * and the ring says when something is.
 */
function FilterSelect({
  icon, label, className, children, ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { icon: IconName; label: string }) {
  const active = Boolean(rest.value);
  return (
    <div className={clsx('relative', className)}>
      <Icon
        name={icon}
        size={14}
        className={clsx(
          'pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2',
          active ? 'text-[var(--ff-primary)]' : 'text-[var(--ff-text-faint)]',
        )}
      />
      <Select
        {...rest}
        aria-label={label}
        className={clsx(
          'truncate py-1 pl-8 text-xs',
          active && 'font-medium ring-2 ring-[var(--ff-primary)]/25',
        )}
      >
        {children}
      </Select>
    </div>
  );
}

/**
 * One response in the feed.
 *
 * The preview columns follow the form's own question order, so responses to the same form
 * line up and the list can be read down a column rather than re-read row by row.
 */
function Row({
  row, selected, compact, onOpen,
}: {
  row: InboxRow;
  selected: boolean;
  compact: boolean;
  onOpen: () => void;
}) {
  return (
    <li data-response={row.id}>
      <button
        type="button"
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
        className={clsx(
          'w-full border-b border-[var(--ff-border)] px-4 py-2.5 text-left transition-colors',
          selected
            ? 'bg-[var(--ff-primary)]/10'
            : 'hover:bg-[var(--ff-surface-raised)]',
        )}
      >
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 truncate text-sm font-medium">{row.form.name}</span>
          <span className="shrink-0 font-mono text-[11px] text-[var(--ff-text-muted)]">
            #{row.sequenceNumber}
          </span>
          <span className="ml-auto shrink-0 text-[11px] text-[var(--ff-text-muted)]">
            {relative(row.submittedUtc)}
          </span>
        </div>

        <div className="mt-1 space-y-0.5">
          {row.preview.length === 0 ? (
            <p className="text-xs italic text-[var(--ff-text-muted)]">No answers recorded</p>
          ) : row.preview.slice(0, compact ? 2 : 3).map((cell) => (
            <p key={cell.questionRef} className="flex gap-2 text-xs">
              <span className="w-28 shrink-0 truncate text-[var(--ff-text-muted)]"
                    title={cell.label}>
                {cell.label}
              </span>
              <span className={clsx(
                'min-w-0 truncate',
                cell.pii && 'font-mono text-[var(--ff-text-muted)]',
              )}>
                {cell.value}
              </span>
            </p>
          ))}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <Badge>{row.channel}</Badge>
          {row.status !== 'complete' && <Badge tone="warning">{row.status}</Badge>}
          {row.score && (
            <Badge tone={row.score.passed ? 'success' : 'danger'}>
              {Math.round(row.score.percentage)}%
            </Badge>
          )}
          {row.hasPii && (
            <span title="Contains personal data"
                  className="text-[var(--ff-text-muted)]">
              <Icon name="shield" size={12} />
            </span>
          )}
          {row.identity && (
            <span className="max-w-[10rem] truncate text-[11px] text-[var(--ff-text-muted)]">
              {row.identity}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}
